import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { finalize } from 'rxjs';
import { TaskService } from '../../../services/task.service';
import { TaskTimelineEvent } from '../tasks.types';
import { getPersonName } from '../tasks.utils';

type TimelineFilter = 'all' | 'comments' | 'history';

/**
 * Comments and history of a task, the newest first, and the field to write a comment. Every change of the comments
 * is saved right away and reported, so the owner reads the events again.
 */
@Component({
  selector: 'app-task-timeline',
  templateUrl: './task-timeline.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block' },
})
export class TaskTimelineComponent {
  @Input({ required: true }) taskId!: string;
  @Input() events: TaskTimelineEvent[] = [];
  @Input() loading = false;
  @Output() changed = new EventEmitter<void>();

  private readonly taskService = inject(TaskService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly filters: { key: TimelineFilter; labelKey: string }[] = [
    { key: 'all', labelKey: 'crm_tasks.timeline.filter_all' },
    { key: 'comments', labelKey: 'crm_tasks.timeline.filter_comments' },
    { key: 'history', labelKey: 'crm_tasks.timeline.filter_history' },
  ];
  protected filter: TimelineFilter = 'all';

  protected readonly commentControl = new FormControl('', { nonNullable: true });
  protected sending = false;

  /** The comment that is being edited, with its text */
  protected editingId: string | null = null;
  protected readonly editControl = new FormControl('', { nonNullable: true });
  protected saving = false;

  protected readonly getPersonName = getPersonName;

  protected get visibleEvents(): TaskTimelineEvent[] {
    if (this.filter === 'all') return this.events;

    const comments = this.filter === 'comments';
    return this.events.filter((event) => (event.event_type === 'COMMENT') === comments);
  }

  protected get canSend(): boolean {
    return !!this.commentControl.value.trim() && !this.sending;
  }

  protected getTitleKey(event: TaskTimelineEvent): string {
    switch (event.sub_type) {
      case 'STATUS_CHANGE':
        return 'crm_tasks.timeline.status_changed';
      case 'DEADLINE_CHANGE':
        return 'crm_tasks.timeline.deadline_changed';
      default:
        return event.action === 'CREATE' ? 'crm_tasks.timeline.created' : 'crm_tasks.timeline.updated';
    }
  }

  protected getIcon(event: TaskTimelineEvent): string {
    switch (event.sub_type) {
      case 'STATUS_CHANGE':
        return 'swap_horiz';
      case 'DEADLINE_CHANGE':
        return 'event';
      default:
        return event.action === 'CREATE' ? 'add_task' : 'edit';
    }
  }

  protected getComment(event: TaskTimelineEvent): string {
    return event.x_data?.comment ?? '';
  }

  protected send(): void {
    const text = this.commentControl.value.trim();
    if (!text || this.sending) return;

    this.sending = true;
    this.taskService
      .createComment(this.taskId, text)
      .pipe(
        finalize(() => (this.sending = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.commentControl.reset('');
          this.changed.emit();
        },
        error: (error) => this.taskService.error(error),
      });
  }

  protected startEdit(event: TaskTimelineEvent): void {
    this.editingId = event.id;
    this.editControl.setValue(this.getComment(event));
  }

  protected cancelEdit(): void {
    this.editingId = null;
  }

  protected saveEdit(event: TaskTimelineEvent): void {
    const text = this.editControl.value.trim();
    if (!text || this.saving) return;

    this.saving = true;
    this.taskService
      .updateComment(this.taskId, event.id, text)
      .pipe(
        finalize(() => (this.saving = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.editingId = null;
          this.changed.emit();
        },
        error: (error) => this.taskService.error(error),
      });
  }

  protected remove(event: TaskTimelineEvent): void {
    this.taskService
      .confirm('crm_tasks.timeline.delete_confirm')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.taskService
          .deleteComment(this.taskId, event.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => this.changed.emit(),
            // Someone deleted it before: the list is what is out of date
            error: (error) => {
              this.taskService.error(error);
              this.changed.emit();
            },
          });
      });
  }
}
